import * as React from 'react';
import * as _ from 'lodash';
import {
  ControlState,
  ControlStyleClass,
  DisplayData,
  hashString,
  Logging,
  renderSelect,
} from '@vcd-ecm-ui/Common';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  DroppableProvided,
  DroppableStateSnapshot,
  DropResult,
  DraggableLocation,
} from 'react-beautiful-dnd';
import {
  VtxButton,
  VtxCheckbox,
  VtxIcon,
  VtxModal,
} from '@vertexinc/vtx-ui-react-component-library';
import TableManagementProps from './TableManagementProps.interface';
import './table-management.scss';
import TableManagementColumn from './TableManagementColumn';
import {
  SaveFiltersSorterEnum,
  ShowBreadCrumbsEnum,
  TableColumn,
  TableSettings,
} from '../..';

interface MoveResult {
  source: TableColumn[];
  destination: TableColumn[];
}

// Accessibility in Drag & drop
// https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/sensors/keyboard.md

// Customiz the Screen Reader
// https://egghead.io/lessons/react-customize-screen-reader-messages-for-drag-and-drop-with-react-beautiful-dnd

const styles: ControlStyleClass = null;

const controlStateEnabled: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const bodyStyle = {
  backgroundColor: 'white',
};

const TableManagement = ({
  isOpen,
  onOK = null,
  onCancel = null,
  tableKey,
  defaultTableSettings,
  leftPinnedColumnsVisible = false,
  rightPinnedColumnsVisible = false,
  tableManagementVisible = false,
}: TableManagementProps): JSX.Element => {
  const [leftItems, setLeftItems] = React.useState([]);
  const [centerItems, setCenterItems] = React.useState([]);
  const [rightItems, setRightItems] = React.useState([]);

  const [showBreadcrumbs, setShowBreadcrumbs] = React.useState(null);
  const [saveFiltersSorter, setSaveFiltersSorter] = React.useState(null);
  const [hideDisabledColumns, setHideDisabledColumns] = React.useState(false);

  // Close the Table Management
  const onCloseDialog = (): void => {
    Logging.Debug('Table Settings Close');
    onCancel();
  };

  // Reorder the List
  const reorderList = (list: TableColumn[], startIndex: number, endIndex: number): TableColumn[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  // Move Items Between the Lists
  const moveBetweenList = (
    source: TableColumn[],
    destination: TableColumn[],
    droppableSource: DraggableLocation,
    droppableDestination: DraggableLocation,
  ): MoveResult => {
    const sourceClone = Array.from(source);
    const destinationClone = Array.from(destination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);

    destinationClone.splice(droppableDestination.index, 0, removed);

    const result: MoveResult = {
      source: sourceClone,
      destination: destinationClone,
    };

    return result;
  };

  // Style of the Drag Item
  const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
    // Some basic styles to make the items look a bit nicer
    userSelect: 'none',
    padding: '1px 5px 1px 5px',
    margin: '0 0 5px 0',
    border: '1px solid black',
    borderRadius: '5px',

    // Change background colour if dragging
    background: isDragging ? '#79c4fc' : '#FFFFFF',
    color: isDragging ? '#FFFFFF' : '#000000',

    // styles we need to apply on draggables
    ...draggableStyle,
  });

  // Style of the Drop Canvas
  const getListStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? '#E6F6FF' : '#F5F5F5',
    padding: '10px',
    width: '100%',
    border: '1px solid darkgrey',
    borderRadius: '5px',
    overflow: 'auto',
  });

  // Get the List Items
  const getList = (id: string): TableColumn[] => {
    switch (id) {
      case 'droppableLeft':
        return leftItems;
      case 'droppableCenter':
        return centerItems;
      case 'droppableRight':
        return rightItems;
      default:
        return [];
    }
  };

  // Get the Visible List
  const getListVisible = (id: string): TableColumn[] => {
    if (!hideDisabledColumns) {
      return getList(id);
    }

    switch (id) {
      case 'droppableLeft':
        return leftItems.filter((column: TableColumn) => column.visibility === true);
      case 'droppableCenter':
        return centerItems.filter((column: TableColumn) => column.visibility === true);
      case 'droppableRight':
        return rightItems.filter((column: TableColumn) => column.visibility === true);
      default:
        return [];
    }
  };

  // Set the List Items
  const setList = (id: string, list: TableColumn[]) => {
    Logging.Debug('Setting List', id, list);

    switch (id) {
      case 'droppableLeft':
        setLeftItems(list);
        break;
      case 'droppableCenter':
        setCenterItems(list);
        break;
      case 'droppableRight':
        setRightItems(list);
        break;
      default:
        break;
    }
  };

  // Dragging has Ended
  const onDragEnd = (result: DropResult) => {
    Logging.Debug('onDragEnd', result);

    // Dropped outside the Lists
    if (!result.destination) {
      return;
    }

    // No Movement
    if ((result.destination.droppableId === result.source.droppableId) && (result.destination.index === result.source.index)) {
      return;
    }

    // Item is Dropped Left - Check to see if the Item is not dropped ABOVE any Left items that cannot move
    if (result.destination.droppableId === 'droppableLeft') {
      let index = 0;
      let canDrop = true;

      leftItems?.forEach((column: TableColumn) => {
        if (column.canMove === false) {
          // Tried to move Above Unmoveable item
          if (result.destination.index <= index) {
            canDrop = false;
          }
        }

        index += 1;
      });

      if (!canDrop) {
        return;
      }
    }

    // Item is Dropped Right - Check to see if the Item is not dropped BELOW any Right items that cannot move
    if (result.destination.droppableId === 'droppableRight') {
      let index = 0;
      let canDrop = true;

      rightItems?.forEach((column: TableColumn) => {
        if (column.canMove === false) {
          // Moved inside Right List
          if (result.destination.droppableId === result.source.droppableId) {
            // Tried to move Below Unmoveable item
            if (result.destination.index >= index) {
              canDrop = false;
            }
          } else if (result.destination.index > index) {
            canDrop = false;
          }
        }

        index += 1;
      });

      if (!canDrop) {
        return;
      }
    }

    // Moved between Lists
    if (result.destination.droppableId !== result.source.droppableId) {
      const list = getList(result.source.droppableId);
      const dragItem = list[result.source.index];

      // The Pinning of the Item cannot be Changed
      if (dragItem && !dragItem.canChangePin) {
        return;
      }

      const lists = moveBetweenList(getList(result.source.droppableId), getList(result.destination.droppableId), result.source, result.destination);

      setList(result.source.droppableId, lists.source);
      setList(result.destination.droppableId, lists.destination);
    } else { // Reorder the Items inside List
      const newItems: TableColumn[] = reorderList(getList(result.destination.droppableId), result.source.index, result.destination.index);
      setList(result.destination.droppableId, newItems);
    }
  };

  // Update the Column Visibility
  const updateTableColumnVisibility = (listId: string, index: number, visibility: boolean): void => {
    const clone = Array.from(getList(listId));

    clone[index].visibility = visibility;

    setList(listId, clone);
  };

  // Select / Unselect Columns
  const selectItemsInList = (id: string, visibility: boolean): void => {
    const clone = Array.from(getList(id));

    clone.forEach((column: TableColumn) => {
      // eslint-disable-next-line no-param-reassign
      column.visibility = !column.canChangeVisibilty ? column.visibility : visibility;
    });

    setList(id, clone);
  };

  // Select all Columns
  const onSelectAll = (): void => {
    selectItemsInList('droppableLeft', true);
    selectItemsInList('droppableCenter', true);
    selectItemsInList('droppableRight', true);

    setHideDisabledColumns(false);
  };

  // Unselect all Columns that can be unselected
  const onClearAll = (): void => {
    selectItemsInList('droppableLeft', false);
    selectItemsInList('droppableCenter', false);
    selectItemsInList('droppableRight', false);

    setHideDisabledColumns(false);
  };

  // Parse Table Settings
  const parseTableSettings = (settings: TableSettings): void => {
    if (settings) {
      const cloneSettings = _.cloneDeep(settings);

      const left: TableColumn[] = [];
      const center: TableColumn[] = [];
      const right: TableColumn[] = [];

      cloneSettings.columns.forEach((column: TableColumn) => {
        switch (column.pin) {
          case 'left':
            left.push(column);
            break;
          case 'right':
            right.push(column);
            break;
          default:
            center.push(column);
            break;
        }
      });

      setLeftItems(left);
      setCenterItems(center);
      setRightItems(right);

      setHideDisabledColumns(settings.hideDisabledColumns);
      setSaveFiltersSorter(settings.saveFiltersSorter);
      setShowBreadcrumbs(settings.showBreadcrumbs);
    }
  };

  // OK Button was Pressed
  const onOKLocal = (): void => {
    const tableSettings: TableSettings = {
      version: 'v1',
      tableKey,
      hideDisabledColumns,
      saveFiltersSorter,
      showBreadcrumbs,
      columns: [],
    };

    leftItems.forEach((column: TableColumn) => {
      // eslint-disable-next-line no-param-reassign
      column.pin = 'left';
      tableSettings.columns.push(column);
    });

    centerItems.forEach((column: TableColumn) => {
      // eslint-disable-next-line no-param-reassign
      column.pin = undefined;
      tableSettings.columns.push(column);
    });

    rightItems.forEach((column: TableColumn) => {
      // eslint-disable-next-line no-param-reassign
      column.pin = 'right';
      tableSettings.columns.push(column);
    });

    // Save the Settings to the Local Storage.  If the Settings are the same as the Default Settings clear the Local Storage
    const settings: string = _.isEqual(tableSettings, defaultTableSettings) ? null : JSON.stringify(tableSettings);

    // Update the Local Storage
    localStorage[tableKey] = settings;

    // Create a Hash of the String to the Grid knowns is settings has changed
    const settingsHash: string = settings ? hashString(settings) : null;

    Logging.Info('Table Settings OK:', settingsHash, settings ? tableSettings : 'Default');

    onOK(settingsHash);
  };

  // Reset the Table Settings to the Default values
  const onReset = (): void => {
    Logging.Debug('Reset Table Settings', defaultTableSettings);

    parseTableSettings(defaultTableSettings);
  };

  const getSaveFilterSorterOptions = (): DisplayData[] => {
    const options: DisplayData[] = [
      {
        data: SaveFiltersSorterEnum.NEVER,
        display: 'Never',
      },
      {
        data: SaveFiltersSorterEnum.SESSION_STORAGE,
        display: 'For Browser Session',
      },
      {
        data: SaveFiltersSorterEnum.LOCAL_STORAGE,
        display: 'For Browser',
      },
    ];

    return options;
  };

  const getBreadcrumbOptions = (): DisplayData[] => {
    const options: DisplayData[] = [
      {
        data: ShowBreadCrumbsEnum.NEVER,
        display: 'Never',
      },
      {
        data: ShowBreadCrumbsEnum.VISIBLE,
        display: 'For Visible Columns only',
      },
      {
        data: ShowBreadCrumbsEnum.HIDDEN,
        display: 'For Hidden Columns only',
      },
      {
        data: ShowBreadCrumbsEnum.ALL,
        display: 'For All Columns',
      },
    ];

    return options;
  };

  // Initial Load
  React.useEffect(() => {
    // Read the Table Settings from the Local Storage
    const tableSettings: TableSettings = localStorage[tableKey] ? JSON.parse(localStorage[tableKey]) : null;

    Logging.Debug('Table Settings:', tableKey, tableSettings, defaultTableSettings);

    // Use the Table Settings in Local Storage, otherwise use the Default Table Settings
    parseTableSettings(tableSettings || defaultTableSettings);
  }, []);

  const Spacer = () => <span style={{ marginRight: '10px' }} />;

  // eslint-disable-next-line max-len
  const leftCount = hideDisabledColumns ? `${leftItems.filter((column: TableColumn) => column.visibility === true).length}` : `${leftItems.filter((column: TableColumn) => column.visibility === true).length} / ${leftItems.length}`;

  // eslint-disable-next-line max-len
  const centerCount = hideDisabledColumns ? `${centerItems.filter((column: TableColumn) => column.visibility === true).length}` : `${centerItems.filter((column: TableColumn) => column.visibility === true).length} / ${centerItems.length}`;

  // eslint-disable-next-line max-len
  const rightCount = hideDisabledColumns ? `${rightItems.filter((column: TableColumn) => column.visibility === true).length}` : `${rightItems.filter((column: TableColumn) => column.visibility === true).length} / ${rightItems.length}`;

  return (
    <div>
      <VtxModal
        visible={isOpen}
        onCancel={(): void => onCloseDialog()}
        width="50%"
        bodyStyle={bodyStyle}
        footer={null}
        closable={false}
      >
        <div className="tableManagementContainer">
          {
            tableManagementVisible && (
              <>
                <div className="tableManagementHeading">
                  <div className="tableManagementHeadingLeft">
                    Table Settings
                  </div>
                </div>
                <div className="tableManagementConfiguration">
                  {renderSelect(33, 'saveFiltersSorters', 'Save Paging, Filters & Sorting', null, false, true,
                    getSaveFilterSorterOptions(), true, null, saveFiltersSorter, setSaveFiltersSorter, controlStateEnabled, styles, null)}
                  {renderSelect(33, 'breadcrumbs', 'Show Breadcrumbs', null, false, true,
                    getBreadcrumbOptions(), true, null, showBreadcrumbs, setShowBreadcrumbs, controlStateEnabled, styles, null)}
                </div>
              </>
            )
          }
          <div className="tableManagementHeading">
            <div className="tableManagementHeadingLeft">
              Column Settings
            </div>
            <div className="tableManagementHeadingRight">
              <VtxCheckbox
                id="visibleCheck"
                name="visibleCheck"
                checked={hideDisabledColumns}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(event: any): void => setHideDisabledColumns(event.target.checked)}
              >
                Hide all disabled columns
              </VtxCheckbox>
            </div>
          </div>
          <div className="tableManagementMessage">
            {/* eslint-disable-next-line max-len */}
            Configure, arrange and move between groups the Columns as needed using Drag &amp; Drop or the Keyboard (use Space to select).
          </div>
          <div className="tableManagementZonesHeader">
            {
              leftPinnedColumnsVisible && (
                <>
                  <div className="tableManagementZonesHeaderName">
                    <span>{`Left Pinned Columns: ${leftCount}`}</span>
                    <div className="tableManagementZonesHeaderDescription">
                      Columns always shown on the Left that do not scroll horizontal.
                    </div>
                  </div>
                  <Spacer />
                </>
              )
            }
            <div className="tableManagementZonesHeaderName">
              <span>{`Normal Columns: ${centerCount}`}</span>
              <div className="tableManagementZonesHeaderDescription">
                Columns that can be scrolled horizontally.
              </div>
            </div>
            {
              rightPinnedColumnsVisible && (
                <>
                  <Spacer />
                  <div className="tableManagementZonesHeaderName">
                    <span>{`Right Pinned Columns: ${rightCount}`}</span>
                    <div className="tableManagementZonesHeaderDescription">
                      A description of Right Pinning will go here.
                    </div>
                  </div>
                </>
              )
            }
          </div>
          <div className="tableManagementZones">
            <DragDropContext onDragEnd={onDragEnd}>
              {
                leftPinnedColumnsVisible && (
                  <>
                    <Droppable droppableId="droppableLeft">
                      {(droppableProvided: DroppableProvided, droppableSnapshot: DroppableStateSnapshot) => (
                        <div
                          {...droppableProvided.droppableProps}
                          ref={droppableProvided.innerRef}
                          style={getListStyle(droppableSnapshot.isDraggingOver)}
                        >
                          {getListVisible('droppableLeft')?.map((item: TableColumn, index: number) => (
                            <Draggable key={item.dataIndex} draggableId={item.dataIndex} index={index} isDragDisabled={!item.canMove}>
                              {(draggableProvided: DraggableProvided, draggableSnapshot: DraggableStateSnapshot) => (
                                <div
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                  style={getItemStyle(
                                    draggableSnapshot.isDragging,
                                    draggableProvided.draggableProps.style,
                                  )}
                                >
                                  <TableManagementColumn
                                    column={item}
                                    index={index}
                                    listId="droppableLeft"
                                    isDragging={draggableSnapshot.isDragging}
                                    updateTableColumnVisibility={updateTableColumnVisibility}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {droppableProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                    <Spacer />
                  </>
                )
              }
              <Droppable droppableId="droppableCenter">
                {(droppableProvided: DroppableProvided, droppableSnapshot: DroppableStateSnapshot) => (
                  <div
                    {...droppableProvided.droppableProps}
                    ref={droppableProvided.innerRef}
                    style={getListStyle(droppableSnapshot.isDraggingOver)}
                  >
                    {getListVisible('droppableCenter')?.map((item: TableColumn, index: number) => (
                      <Draggable key={item.dataIndex} draggableId={item.dataIndex} index={index} isDragDisabled={!item.canMove}>
                        {(draggableProvided: DraggableProvided, draggableSnapshot: DraggableStateSnapshot) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            {...draggableProvided.dragHandleProps}
                            style={getItemStyle(
                              draggableSnapshot.isDragging,
                              draggableProvided.draggableProps.style,
                            )}
                          >
                            <TableManagementColumn
                              column={item}
                              index={index}
                              listId="droppableCenter"
                              isDragging={draggableSnapshot.isDragging}
                              updateTableColumnVisibility={updateTableColumnVisibility}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
              {
                rightPinnedColumnsVisible && (
                  <>
                    <Spacer />
                    <Droppable droppableId="droppableRight">
                      {(droppableProvided: DroppableProvided, droppableSnapshot: DroppableStateSnapshot) => (
                        <div
                          {...droppableProvided.droppableProps}
                          ref={droppableProvided.innerRef}
                          style={getListStyle(droppableSnapshot.isDraggingOver)}
                        >
                          {getListVisible('droppableRight')?.map((item: TableColumn, index: number) => (
                            <Draggable key={item.dataIndex} draggableId={item.dataIndex} index={index} isDragDisabled={!item.canMove}>
                              {(draggableProvided: DraggableProvided, draggableSnapshot: DraggableStateSnapshot) => (
                                <div
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                  style={getItemStyle(
                                    draggableSnapshot.isDragging,
                                    draggableProvided.draggableProps.style,
                                  )}
                                >
                                  <TableManagementColumn
                                    column={item}
                                    index={index}
                                    listId="droppableRight"
                                    isDragging={draggableSnapshot.isDragging}
                                    updateTableColumnVisibility={updateTableColumnVisibility}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {droppableProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </>
                )
              }
            </DragDropContext>
          </div>
          <div className="tableManagementButtons">
            <div className="tableManagementButtonsLeft">
              <div className="buttonItemBase">
                <VtxButton
                  id="resetButton"
                  onClick={onReset}
                  icon={<VtxIcon type="rollback" />}
                  type="default"
                >
                  Reset
                </VtxButton>
              </div>
              <div className="buttonItemBase">
                <VtxButton
                  id="selectAllButton"
                  icon={<VtxIcon type="check-square" />}
                  onClick={onSelectAll}
                  type="default"
                >
                  Select All
                </VtxButton>
              </div>
              <div className="buttonItemBase">
                <VtxButton
                  id="clearAllButton"
                  icon={<VtxIcon type="border" />}
                  onClick={onClearAll}
                  type="default"
                >
                  Clear All
                </VtxButton>
              </div>
            </div>
            <div className="tableManagementButtonsRight">
              <div className="buttonItemBase">
                <VtxButton
                  id="cancelButton"
                  onClick={onCancel}
                  icon={<VtxIcon type="stop" />}
                  type="default"
                >
                  Cancel
                </VtxButton>
              </div>
              <div className="buttonItemBase">
                <VtxButton
                  id="okButton"
                  onClick={onOKLocal}
                  type="primary"
                >
                  Save
                </VtxButton>
              </div>
            </div>
          </div>
        </div>
      </VtxModal>
    </div>
  );
};

export default TableManagement;
